import { useEffect, useState } from 'react';
import { deleteBlob } from '../lib/blobStore';
import { getJobGps, type GpsCoord } from '../lib/gps';
import { createSession, type CreatedSession } from '../lib/session';
import { enqueue } from '../lib/syncQueue';
import { getDefaultTaxonomy, getTaxonomy, type Taxonomy } from '../lib/taxonomy';
import { TaxonomyDrawer } from './TaxonomyDrawer';
import { SessionGallery } from './SessionGallery';
import { NotepadSheet } from './NotepadSheet';
import { advance, contextForState, initialState, type SequenceState } from './openingSequence';
import { PostShotOverlay } from './PostShotOverlay';
import {
  EMPTY_ELEVATION_COUNTS,
  type CapturedPhoto,
  type ElevationCounts,
  type ShotContext,
} from './types';
import { Viewfinder } from './Viewfinder';

interface Props {
  jobId: string;
  mode: 'new' | 'reinspection';
  reinspectionType?: 'new_set' | 'add_to_existing';
  gapPreserved?: boolean;
  onWrapUp: () => void;
}

export function CameraScreen({
  jobId,
  mode,
  reinspectionType,
  gapPreserved,
  onWrapUp,
}: Props) {
  const [seq, setSeq] = useState<SequenceState>(initialState());
  const [taxonomy, setTaxonomy] = useState<Taxonomy | null>(null);
  const [defaults, setDefaults] = useState<Taxonomy | null>(null);
  const [session, setSession] = useState<CreatedSession | null>(null);
  const [, setGps] = useState<GpsCoord | null>(null);
  const [gpsRef, setGpsRef] = useState<GpsCoord | null>(null);
  const [inFlight, setInFlight] = useState<CapturedPhoto | null>(null);
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [notepadOpen, setNotepadOpen] = useState(false);
  const [notepadCount, setNotepadCount] = useState(0);
  const [overrideContext, setOverrideContext] = useState<ShotContext | null>(null);
  const [photoCount, setPhotoCount] = useState(0);
  const [elevationCounts, setElevationCounts] = useState<ElevationCounts>({
    ...EMPTY_ELEVATION_COUNTS,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tax, def, sess] = await Promise.all([
          getTaxonomy(),
          getDefaultTaxonomy(),
          createSession({
            job_id: jobId,
            mode,
            reinspection_type: reinspectionType,
            gap_preserved: gapPreserved ?? false,
          }),
        ]);
        if (cancelled) return;
        setTaxonomy(tax);
        setDefaults(def);
        setSession(sess);
        getJobGps(jobId).then((coord) => {
          if (cancelled) return;
          setGps(coord);
          setGpsRef(coord);
        });
      } catch (e) {
        if (!cancelled) setError(`bootstrap failed: ${(e as Error).message}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId, mode, reinspectionType, gapPreserved]);

  if (error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black p-4 text-center text-white">
        <div>
          <div className="text-lg font-semibold text-red-400">Camera unavailable</div>
          <div className="mt-2 text-sm opacity-80">{error}</div>
        </div>
      </div>
    );
  }
  if (!taxonomy || !defaults || !session) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black text-white">
        <div className="text-sm opacity-70">Setting up inspection…</div>
      </div>
    );
  }

  const sequenceContext = contextForState(seq);
  const currentContext =
    seq.phase === 'free_flow' && overrideContext ? overrideContext : sequenceContext;

  function handleShutter(photo: CapturedPhoto) {
    setInFlight(photo);
  }

  async function handleConfirm(finalPhoto: CapturedPhoto) {
    const sess = session!;
    const sequenceIndex = photoCount + 1;
    await enqueue({
      id: crypto.randomUUID(),
      org_id: '', // backend derives from JWT; client value is ignored
      job_id: jobId,
      session_id: sess.id,
      photo_id: finalPhoto.id,
      sequence_index: sequenceIndex,
      local_blob_key: finalPhoto.blobKey,
      metadata: {
        captured_at: finalPhoto.capturedAt,
        checksum: finalPhoto.checksum,
        device_info: {
          ua: navigator.userAgent,
          gps: gpsRef
            ? { lat: gpsRef.lat, lng: gpsRef.lng, accuracy_m: gpsRef.accuracy_m }
            : null,
          context: {
            category: finalPhoto.context.category,
            label: finalPhoto.context.label,
            description: finalPhoto.context.description,
            elevation: finalPhoto.context.elevation,
          },
          flagged: finalPhoto.flagged,
          note_text: finalPhoto.noteText,
        },
      },
    });
    setSeq((s) => advance(s));
    setPhotoCount((n) => n + 1);
    if (finalPhoto.context.elevation) {
      const elev = finalPhoto.context.elevation;
      setElevationCounts((ec) => ({ ...ec, [elev]: ec[elev] + 1 }));
    }
    setInFlight(null);
  }

  async function handleShootAgain() {
    if (inFlight) {
      try {
        await deleteBlob(inFlight.blobKey);
      } catch {
        // best-effort cleanup
      }
    }
    setInFlight(null);
  }

  function handleSelectContext(next: ShotContext) {
    setOverrideContext(next);
  }

  return (
    <>
      {!inFlight && !galleryOpen && (
        <Viewfinder
          context={currentContext}
          taxonomy={taxonomy}
          photoCount={photoCount}
          notepadCount={notepadCount}
          showWrapUp={seq.phase === 'free_flow'}
          onShutter={handleShutter}
          onWrapUp={onWrapUp}
          onOpenContext={() => setBottomSheetOpen(true)}
          onContextChange={handleSelectContext}
          onTaxonomyMutated={setTaxonomy}
          onOpenNotepad={() => setNotepadOpen(true)}
          onOpenGallery={() => setGalleryOpen(true)}
        />
      )}
      {galleryOpen && (
        <SessionGallery jobId={jobId} onClose={() => setGalleryOpen(false)} />
      )}
      {notepadOpen && (
        <NotepadSheet
          jobId={jobId}
          onClose={() => setNotepadOpen(false)}
          onCountChange={setNotepadCount}
        />
      )}
      {inFlight && (
        <PostShotOverlay
          photo={inFlight}
          taxonomy={taxonomy}
          defaults={defaults}
          elevationCounts={elevationCounts}
          onConfirm={handleConfirm}
          onShootAgain={handleShootAgain}
          onTaxonomyMutated={setTaxonomy}
        />
      )}
      {bottomSheetOpen && (
        <TaxonomyDrawer
          taxonomy={taxonomy}
          defaults={defaults}
          current={currentContext}
          onSelect={handleSelectContext}
          onClose={() => setBottomSheetOpen(false)}
          onTaxonomyMutated={setTaxonomy}
        />
      )}
    </>
  );
}
